import adConfig from './adslots';
import { getPPID } from './ppid';
import adsettings from './adsettings';
import amaLog, { amaWarn } from '../helpers/log';
import { getModules } from '../modules';
import { timeFor } from '../helpers/time';
import { pbjs } from '../prebid';
import getConsent from '../consent/consent';
import { getDmVerticals } from './verticals';
import { getHostName, getSiteName, isVideoPage } from './domain';
import { getPageType } from './device';
/////////////////
//////////////////////////////////////////////////////////////////////////////////////
/////////

let verticalsLoaded = false;
let verticals: string[] | undefined = undefined;

const getVerticals = (configVerticals?: IVerticals) => {
  verticals = verticals || getDmVerticals(configVerticals);
  return verticals;
};

const isAdTag = (adTag?: string) => adTag?.includes('doubleclick.net');
/**
 * Resolves the ad schedule ID based on the provided configuration and start type.
 * @param config The ad configuration.
 * @param startType The type of ad start.
 * @returns The resolved ad schedule ID.
 */
const resolveAdScheduleId = (config: any, startType: string, pageType: string): string => {
  if (!config?.video || isAdTag(config.video)) {
    console.error('Invalid ad configuration:', config);
    return null;
  }
  if (pageType === 'article') {
    return config.video?.[startType];
  }
  if (pageType === 'videopage') {
    return config.videopage?.[startType];
  }
  return null;
};

const getGamAdTag = (config: IAdsConfig, startType: string): string | null => {
  let adTag;
  const { adTags } = getModules();
  // Check if adTags should be used
  if (!adTags) {
    console.warn('AdTag - No adTags defined in module configuration');
    return null;
  }
  if (!config) {
    console.warn('AdTag - No accConfig configuration found');
  } else {
    // Try use adTag from adConfig.video if it is set there (old value is scheduleId)
    if (isVideoPage() && config.videopage?.[startType]) {
      adTag = config.videopage?.[startType];
      amaLog(`AdTag - Try get from accConfig.videopage: ${adTag}`);
    } else {
      adTag = config.video?.[startType];
      amaLog('AdTag - Try get from accConfig.video', adTag);
    }
  }

  if (!isAdTag(adTag)) {
    // AdTag from adConfig is invalid, try to get from module adTags
    amaLog('Invalid GAM ad tag in accConfig.video/videopage:', adTag);
    if (adTags?.[startType]) {
      adTag = adTags[startType];
      amaLog('Use ad tag in module adTags:', adTag);
    } else {
      amaWarn(`AdTag - No adTag found for startType: ${startType} in module adTags`);
      adTag = null;
    }
  }
  return adTag;
};

const requestPrebidTag = (adUnitPath: string, playerSize: [number, number], usePoliticalAds: boolean = false): Promise<string | undefined> => {
  const funcTimeout = 15000;
  const prebidTimeout = 4500;

  return new Promise((resolve, reject) => {
    // Fallback if prebid never executes
    let tagTimeoutId = setTimeout(() => {
      cleanUp();
      reject(new Error(`AdTag Timeout (${funcTimeout} ms) for requestPrebidTag() ${adUnitPath}`));
    }, funcTimeout);
    let prebidTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanUp = () => {
      if (tagTimeoutId) {
        clearTimeout(tagTimeoutId);
        tagTimeoutId = null;
      }
      if (prebidTimeoutId) {
        clearTimeout(prebidTimeoutId);
        prebidTimeoutId = null;
      }
    };

    const handleCustomPrebidResponse = (adTagUrl: any) => {
      amaLog(`AdTag - handlePrebidResponse ${timeFor(`prebidVideo ${adUnitPath}`)}`, adTagUrl);
      cleanUp();
      resolve(adTagUrl);
    };
    const handlePrebidResponse = (videoBidResult: any, bids: any) => {
      amaLog(`AdTag - handlePrebidResponse ${timeFor(`prebidVideo ${adUnitPath}`)}`, videoBidResult, bids);
      cleanUp();
      resolve(videoBidResult?.adTagUrl);
    };

    pbjs.que = pbjs.que || [];
    pbjs.que.push(() => {
      amaLog(`AdTag - requestVideoBids() ${timeFor(`prebidVideo ${adUnitPath}`)} `);

      prebidTimeoutId = setTimeout(() => {
        amaLog(`AdTag - Error: Timeout (${prebidTimeout} ms) for pbjs.rp.requestVideoBids()`);
        cleanUp();
        resolve(undefined);
      }, prebidTimeout);
      if (!usePoliticalAds) {
        amaLog('Non Political ads regular adTagUrl #');
        pbjs.rp.requestVideoBids({
          adSlotName: adUnitPath,
          playerSize: playerSize,
          callback: handlePrebidResponse,
          data: {},
        });

      } else {
          pbjs.rp.requestVideoBids({
            adSlotName: adUnitPath,
            playerSize: playerSize,
            adServer: 'custom', 
            callback: (bids: any) => {  
              
              const winner = pbjs.getHighestCpmBids(adUnitPath)[0];
              
              const isPolitical = winner && winner.dealId === 'adn-political';
              
    
              if (isPolitical && winner.vastUrl) {
                const brokenUrl = winner.vastUrl;
                const correctedUrl = brokenUrl.replace("int=dmpbjs?", "int=dmpbjs&");
                amaLog('Political ads adTagUrl #', correctedUrl);
                handleCustomPrebidResponse(correctedUrl); 
              } else {
                const adTagUrl = pbjs.adServers.dfp.buildVideoUrl({ 
                  adUnit: adUnitPath, 
                  params: { 
                    iu: adUnitPath,
                    sz: '1920x1080'
                   } 
                });
                amaLog('Non Political (no winning political ad found) adTagUrl #', adTagUrl);
                handleCustomPrebidResponse(adTagUrl);
              }
            },
            data: {},
          });
      }
    });  
  });
};

const getPrebidAdTag = async (adUnitPath: string, playerSize: [number, number], usePoliticalAds: boolean = false) => {
  if (!adUnitPath) {
    amaLog(`AdTag - adUnitPath is missing `);
    return;
  }
  amaLog(`AdTag - getPrebidAdTag for ('${adUnitPath}')  `);

  enableDMVerticals();

  let tag: any = await requestPrebidTag(adUnitPath, playerSize, usePoliticalAds).catch((error) => {
    amaWarn(`AdTag - Error for requestPrebidTag(${adUnitPath}) `, error);
    return;
  });

  if (tag) {
    amaLog(`AdTag - Prebid video tag FOUND! `, tag);

    return `${tag.replace('securepubads.g', 'pubads.g').replace('output=vast', 'output=xml_vmap1')}`;
  }
};

/**
 * Asynchronously retrieves the ad VAST tag.
 * @param config The type of
 * @param startType The type of ad start: 'clickToPlay' | 'autoPlay'.
 * @returns A Promise that resolves to the ad VAST tag: string | null.
 */
export const getAdTag = async (
  config: IAdsConfig,
  videoConfig: {
    startType?: 'autoPlay' | 'clickToPlay';
    isVertical?: boolean;
    isSecondArticle?: boolean;
  } = {},
): Promise<string | null> => {
  const { isVertical, isSecondArticle } = videoConfig;

  const startType = isVideoPage() ? 'clickToPlay' : videoConfig.startType || 'autoPlay';

  try {
    amaLog(`AdTag - Get Ad tag for startType: ${startType} ${timeFor('getAdTag')} ...`);

    const gamAdTag = getGamAdTag(config, startType);
    if (!gamAdTag) return null;

    let prebidAdTag;
    const hasConsent = await getConsent();
    const usePrebid = window.ama?.adsUseDemandManager === 'true';

    if (usePrebid && hasConsent) {
      const adTagPlayerSize = extractFromQueryString(gamAdTag, 'sz', 'x') as [number, number] | null;
      const playerSize =
        adTagPlayerSize && adTagPlayerSize.length == 2 ? adTagPlayerSize : ([1920,1080] as [number, number]);

      amaLog(`AdTag - Requesting Prebid tag for playerSize: `, playerSize);

      const adUnitPath = extractFromQueryString(gamAdTag, 'iu') as string | null;
      const usePoliticalAds = config?.enablePoliticalAd === 'true';
      if (playerSize && adUnitPath) {
        prebidAdTag = await getPrebidAdTag(adUnitPath, playerSize, usePoliticalAds);
      } else {
        amaWarn(
          `AdTag - Cannot request Prebid tag, missing parameters: playerSize=${playerSize}, adUnitPath=${adUnitPath}`,
        );
      }
    }
    const adTag = prebidAdTag || gamAdTag;

    // Modify Tag with article data
    const urlObj = new URL(adTag);
    const custParamsStr = urlObj.searchParams.get('cust_params') || '';
    const custParams = new URLSearchParams(custParamsStr);

    verticals = getVerticals(config?.verticals);

    if (verticals.length) {
      const v = Array.from(new Set(verticals));
      custParams.set('Vertikal', v.join(','));
    }

    custParams.set('consent', hasConsent ? 'yes' : 'no');
    if (isVertical) custParams.set('page', 'floatingvideo');
    if (isSecondArticle) custParams.set('secondarticle', 'true');
    const explicitContent = adsettings.explicitContent || false; // Todo: verify explicit
    if (explicitContent) custParams.set('explicit', 'true');
    if (prebidAdTag) {
      custParams.set('demandmanager', '1');
      let pos = startType === 'autoPlay' ? 'auto' : 'click';
///////////////////////
      if (isVideoPage()) {
        pos = 'videopage';
      }
///////////////
      custParams.set('pos', pos);
    } else {
      const ppid = getPPID();
      amaLog(`AdTag -ppid: `, typeof ppid, ppid);

      if (ppid && typeof ppid === 'string') urlObj.searchParams.set('ppid', ppid);
    }
    urlObj.searchParams.set('cust_params', custParams.toString());

    const adTagUrl = urlObj.toString();

    amaLog(`AdTag -Resolved adTagUrl: ${timeFor('getAdTag')} `, adTagUrl, custParams.toString());

    return adTagUrl;
  } catch (error) {
    console.error('AdTag - Error retrieving Video Ad tag:', error);
    return null;
  }
};

export const updateMultivideoAdTagUrl = (rawUrl: string, configVerticals: IVerticals) => {
  const urlObj = new URL(rawUrl);

  const custParamsStr = urlObj.searchParams.get('cust_params') || '';
  const custParams = new URLSearchParams(custParamsStr);

  const verticals = getVerticals(configVerticals);
  if (verticals.length) {
    const v = Array.from(new Set(verticals));
    custParams.set('Vertikal', v.join(','));
  }
  custParams.set('pos', 'auto');

  custParams.set('demandmanager', 'exp-pubx');

  urlObj.searchParams.set('cust_params', custParams.toString());

  return urlObj.toString();
};

/**
 * Asynchronously retrieves the ad schedule ID.
 * @param startType The type of ad start.
 * @returns A Promise that resolves to the ad schedule ID.
 */
export const getAdScheduleId = async (
  startType: string = 'autoPlay',
  pageType: string = 'article',
): Promise<string> => {
  try {
    const config = await adConfig.get();
    return resolveAdScheduleId(config, startType, pageType);
  } catch (error) {
    console.error('Error retrieving ad schedule ID:', error);
    return null;
  }
};

export const enableDMVerticals = (configVerticals?: IVerticals) => {
  const { useVerticals } = getModules();

  if (!useVerticals || verticalsLoaded) return;
  verticalsLoaded = true;

  const dmVerticals = getVerticals(configVerticals);

  pbjs.que = pbjs.que || [];
  pbjs.que.push(() => {
    amaLog('AdTag - Setting DM verticals for Prebid:', dmVerticals, getSiteName(), getHostName());

    pbjs.setConfig({
      ortb2: {
        site: {
          name: getSiteName(),
          domain: getHostName(),
          ext: {
            data: {
              page_type: getPageType(),
              inventory: { category: dmVerticals },
              dctr: 'category=' + dmVerticals.join('|category='),
              category: dmVerticals,
            },
          },
        },
      },
    });

/////////////////////
///////////////////////////////////////////////////////////////
/////////////////////////////////////////////

///////////////////////////////////////////////

/////////////////////////////////////////////////////
/////////////////////////////////////////////////////
///////

//////////////////////
//////////////
///////////////
////////////////
///////////////////
////////////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////////////////////////////////////////////
/////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////
//////////////
////////////
//////////
///////////////
////////////////
///////////////////////////////////////////
////////////
//////////
////////
///////

/////////////
  });
};
const extractFromQueryString = (
  queryString: string,
  key: string,
  numberDivider: string = '',
): number[] | string | null => {
  const keyFromUrl = queryString.split(`${key}=`)[1]?.split('&')[0];
  if (!keyFromUrl) return null;

  if (numberDivider !== '') {
    const array = keyFromUrl?.split(numberDivider) || [];
    const nums = array.length > 0 ? array.map((n) => Number(n)) : null;
    return nums?.every((n) => Number.isFinite(n)) ? nums : null;
  } else {
    return keyFromUrl;
  }
};
